import moment from "moment";
import { Contract } from "@edp-components/edp-contract";
import { LastResponse } from "@edp-store/actions/layout.actions";

export class ActiveMeters {
    constructor(
        public meters: Meters[],
        public lastInserted: LastInserted,
        public lastValidReading: LastValidReading,
        public simulation: Simulation,
        public supplyType: string,
        public idMeter: string,
        public selfConsumption: boolean,
        public updateReadingInTime: boolean = false,
        public updateReadingOutTime: boolean = false,
        public hasInsertedOutIRD: boolean = false
    ) {}

    public getRegisterType(registerType: string[]): Meters[] {
        return this.meters.filter(meters => registerType.includes(meters.RegisterType));
    }

    public setActiveMeters = (contract: Contract): ActiveMeters => {
        this.updateReadingInTime = this.hasUpdateReadingInTime(contract);
        this.updateReadingOutTime = this.hasUpdateReadingOutTime(contract);
        this.hasInsertedOutIRD = this.getHasInsertedOutIRD(contract);
        return this;
    }

    private hasUpdateReadingInTime = (contract: Contract): boolean => {
        if (this.lastInserted && this.simulation.simulationValue) {
            const currentDate = moment().startOf("day");
            const Ird_start = moment(contract.Ird_start, "YYYY-MM-DD");
            const Ird_end = moment(contract.Ird_end, "YYYY-MM-DD");
            const lastInsertedDate = moment(this.lastInserted.date, "YYYY-MM-DD");

            const isWithinIRD: boolean = currentDate.isBetween(Ird_start, Ird_end, null, "[]");
            const lastInsertedInIRD: boolean = lastInsertedDate.isBetween(Ird_start, Ird_end, null, "[]");

            return isWithinIRD && lastInsertedInIRD;
        }
        return false;
    }

    private hasUpdateReadingOutTime = (contract: Contract): boolean => {
        if (this.lastInserted && this.simulation.simulationValue) {
            const currentDate = moment().startOf("day");
            const Ird_start = moment(contract.Ird_start, "YYYY-MM-DD");
            const Ird_end = moment(contract.Ird_end, "YYYY-MM-DD");
            const lastInsertedDate = moment(this.lastInserted.date, "YYYY-MM-DD");

            const unbilledPeriod = contract.Ird_end ? Ird_end.subtract(1, "month").add(1, "day") : currentDate;

            const isOutIRD: boolean = currentDate.isBetween(unbilledPeriod, Ird_start, null, "[)");
            const lastInsertedOutIRD: boolean = lastInsertedDate.isBetween(unbilledPeriod, Ird_start, null, "[)");

            if (this.simulation && this.simulation.endBillingDate && isOutIRD && lastInsertedOutIRD) {
                return currentDate.isBefore(this.simulation.endBillingDate);
            }

            return isOutIRD && lastInsertedOutIRD;
        }

        return false;
    }


    private getHasInsertedOutIRD(contract: Contract) {
        const currentDate = moment().format("YYYY-MM-DD");
        const Ird_start = moment(contract.Ird_start, "YYYY-MM-DD");
        const Ird_end = moment(contract.Ird_end, "YYYY-MM-DD");
        const lastInsertedDate = moment(this.lastInserted.date, "YYYY-MM-DD");

        const isWithinIRD: boolean = moment(currentDate, "YYYY-MM-DD").isBetween(Ird_start, Ird_end, undefined, "[]");


        const unbilledPeriod = contract.Ird_end ? Ird_end.subtract(1, "month").add(1, "day") : currentDate;
        const lastInsertedOutIRD: boolean = lastInsertedDate.isBetween(unbilledPeriod, Ird_start, null, "[)");

        return lastInsertedOutIRD && isWithinIRD;
    }
}

export class ActiveMetersFactory {
    static create(activeMeters: ActiveMeters) {
        return new ActiveMeters(
            activeMeters.meters,
            activeMeters.lastInserted,
            activeMeters.lastValidReading,
            activeMeters.simulation,
            activeMeters.supplyType,
            activeMeters.idMeter,
            activeMeters.selfConsumption
        );
    }
}

export class Meters {
    constructor(
        public MinRead: string,
        public MaxRead: string,
        public RegisterType: string,
        public PrevMrType: string,
        public Brand: string,
        public Material: string,
        public Model: string,
        public Serialno: string,
        public Register: string,
        public RegisterTypeStr: string,
        public ReadingResult: string = "0",
        public OutsideConsumption: boolean = false,
        public Predecimalplaces: string = "0"
    ) {}
}

export class MetersFactory {
    static create(meters: Meters) {
        return new Meters(
            meters.MinRead,
            meters.MaxRead,
            meters.RegisterType,
            meters.PrevMrType,
            meters.Brand,
            meters.Material,
            meters.Model,
            meters.Serialno,
            meters.Register,
            meters.RegisterTypeStr,
            meters.ReadingResult,
            meters.OutsideConsumption,
            meters.Predecimalplaces
        );
    }
}

export class LastInserted {
    constructor(
        public date: string,
        public mr_type: string,
        public S: string,
        public FV: string,
        public V: string,
        public C: string,
        public P: string,
        public LT: string,
        public telemetry: string,
        public collectReadingType: string
    ) {}
}

export class LastInsertedFactory {
    static create(lastInserted: LastInserted) {
        return new LastInserted(
            lastInserted.date,
            lastInserted.mr_type,
            lastInserted.S,
            lastInserted.FV,
            lastInserted.V,
            lastInserted.C,
            lastInserted.P,
            lastInserted.LT,
            lastInserted.telemetry,
            lastInserted.collectReadingType
        );
    }
}

export class LastValidReading {
    constructor(
        public date: string,
        public mr_type: string,
        public S: string,
        public FV: string,
        public V: string,
        public C: string,
        public P: string,
        public LT: string,
        public telemetry: string,
        public collectReadingType: string
    ) {}
}

export class LastValidReadingFactory {
    static create(lastValidReading: LastValidReading) {
        return new LastValidReading(
            lastValidReading.date,
            lastValidReading.mr_type,
            lastValidReading.S,
            lastValidReading.FV,
            lastValidReading.V,
            lastValidReading.C,
            lastValidReading.P,
            lastValidReading.LT,
            lastValidReading.telemetry,
            lastValidReading.collectReadingType
        );
    }
}

export class Simulation {
    constructor(
        public simulationValue: string,
        public startBillingDate: string,
        public endBillingDate: string,
        public readingDate: string = null,
        public electricity: string = null,
        public gas: string = null,
        public taxes: string = null,
        public services: string = null,
        public estimatedDays: string = null,
        public realDays: string = null
    ) {}
}

export class SimulationFactory {
    static create(simulation: Simulation) {
        return new Simulation(
            simulation.simulationValue,
            simulation.startBillingDate,
            simulation.endBillingDate,
            simulation.readingDate,
            simulation.electricity,
            simulation.gas,
            simulation.taxes,
            simulation.services,
            simulation.estimatedDays,
            simulation.realDays
        );
    }
}

export class Reading {
    constructor(
        public Brand: string,
        public Material: string,
        public Model: string,
        public SerialNo: string,
        public Register: string,
        public RegisterType: string,
        public RegisterTypeStr: string,
        public ReadingResult: string,
        public ExtUi: string,
        public MrReason: string = "09",
        public ActualCustomerMrType: string = "10",
        public MeterDate: Date = new Date(),
        public InputDateFormat: string = "",
        public OutputDateFormat: string = ""
    ) {}
}

export interface IMeterTypes {
    ModelTypes: {
        ELECTROMECANICO?: IMeterInstruction[];
        INTELIGENTE?: IMeterInstruction[];
        ESTATICO?: IMeterInstruction[];
    };
}

export interface IMeterInstruction {
    Id: string;
    Attributes: {
        Type: string;
        Description: string;
        Picture: string;
    };
    Instructions: {
        Mobile: {
            Vazio: string;
            Ponta: string;
            Cheia: string;
        };
    };
    LastUpdate: string;
}
